// Components/Core/InfoFooter.jsx
import React, { useEffect, useRef } from "react";
import { faEnvelope, faComments } from "@fortawesome/free-solid-svg-icons";
import {
  faWhatsapp,
  faInstagram,
  faTelegram,
} from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { useTenantContent } from "../../Context/TenantContentContext";
import ZarinpalImg from "../../assets/zplogo.png";
import SepImg from "../../assets/seplogo.png";
import { CheckRole } from "../../Services/AuthService";
import Enamad from "./Enamad";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const parseMapCoords = (value) => {
  const parts = String(value || "")
    .split(",")
    .map((part) => Number(String(part).trim()));
  if (parts.length >= 2 && parts.every((n) => Number.isFinite(n))) {
    return [parts[0], parts[1]];
  }
  return [35.70748, 51.38256];
};

const InfoFooter = () => {
  const navigate = useNavigate();
  const { content } = useTenantContent();
  const mapNodeRef = useRef(null);

  useEffect(() => {
    if (!content?.showFooter || !mapNodeRef.current) return;

    const coords = parseMapCoords(content.mapCoordinates);
    const map = L.map(mapNodeRef.current, {
      center: coords,
      zoom: 16,
      scrollWheelZoom: false,
    });

    // Carto raster tiles now require an API key and fail with 403 without it.
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    L.marker(coords).addTo(map);
    const fit = window.setTimeout(() => map.invalidateSize(), 400);

    return () => {
      window.clearTimeout(fit);
      map.remove();
    };
  }, [content]);

  if (!content || !content.showFooter) {
    return null;
  }

  const phoneNumbers = content.phoneNumber
    ? content.phoneNumber.split("\n").filter((p) => p.trim())
    : [];

  return (
    <footer
      className="pb-5 pb-lg-1 pt-4"
      dir="rtl"
      style={{
        backgroundColor: "#1e1e1e",
        color: "#fff",
        fontFamily: "MyPersianFont, sans-serif",
      }}
    >
      <div className="container footer-layout">
        <div className="footer-info-grid">
          <div>
            <h5 style={{ color: "#aaa" }}>بخش های سایت</h5>
            <p className="footer-item" onClick={() => navigate("/")}>
              صفحه اصلی
            </p>
            <p
              className="footer-item"
              onClick={() => navigate("/reservation/reserve")}
            >
              رزرو نوبت
            </p>
            <p
              className="footer-item"
              onClick={() => navigate("/counseling/index")}
            >
              مشاوره آنلاین
            </p>
            <p className="footer-item" onClick={() => navigate("/about")}>
              درباره پزشک
            </p>
            <p className="footer-item" onClick={() => navigate("/blog/index")}>
              مقالات
            </p>
            <p className="footer-item" onClick={() => navigate("/support")}>
              پشتیبانی سایت
            </p>
          </div>

          <div>
            <h5 style={{ color: "#aaa" }}>ارتباط</h5>
            {phoneNumbers.map((phone, index) => (
              <p key={index}>
                <a
                  className="footer-item"
                  href={`tel:${phone.replace(/\s/g, "")}`}
                >
                  {phone}
                </a>
              </p>
            ))}
            {content.email ? (
              <p>
                <a className="footer-item" href={`mailto:${content.email}`}>
                  {content.email}
                </a>
              </p>
            ) : null}
          </div>

          <div>
            <h5 style={{ color: "#aaa" }}>آدرس</h5>
            <p style={{ whiteSpace: "pre-line" }}>
              {content.clinicLocation ? (
                <a
                  className="footer-item"
                  href={content.clinicLocation}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ whiteSpace: "pre-line" }}
                >
                  {content.address}
                </a>
              ) : (
                <span
                  className="footer-item"
                  style={{ whiteSpace: "pre-line" }}
                >
                  {content.address}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="footer-map-block">
          <div className="footer-map-card footer-map-dark">
            <div ref={mapNodeRef} className="footer-map-canvas" />
            {(content.address || content.clinicLocation) && (
              <div className="footer-map-overlay">
                <span>{content.address}</span>
                {content.clinicLocation ? (
                  <a
                    href={content.clinicLocation}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    مشاهده در نقشه
                  </a>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="footer-badges">
          <a
            href="https://www.zarinpal.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={ZarinpalImg} alt="زرین‌پال" />
          </a>
          {Number(content?.paymentGatewayMethod) === 1 && CheckRole(["Admin"]) && (
            <a
              href="https://sep.shaparak.ir"
              target="_blank"
              rel="noopener noreferrer"
              title="درگاه پرداخت سپ (بانک سامان)"
            >
              <img
                src={SepImg}
                alt="پرداخت الکترونیک سامان (سپ)"
                style={{ background: "#ffffff", padding: "6px" }}
              />
            </a>
          )}
          {content.enamadId && content.enamadCode ? <Enamad /> : null}
        </div>
        <div style={{ marginBottom: "10px" }}>
          {content.whatsappNumber && (
            <a
              href={`https://wa.me/${content.whatsappNumber.replace(
                /\D/g,
                "",
              )}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faWhatsapp} style={iconStyle} />
            </a>
          )}

          {content.telegramUrl && (
            <a
              href={content.telegramUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faTelegram} style={iconStyle} />
            </a>
          )}

          <a
            href={content.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FontAwesomeIcon icon={faInstagram} style={iconStyle} />
          </a>

          <a
            href="mailto:mhaghili81@gmail.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FontAwesomeIcon icon={faEnvelope} style={iconStyle} />
          </a>

          <a href="/support">
            <FontAwesomeIcon icon={faComments} style={iconStyle} />
          </a>
        </div>

        <div dir="ltr" className="pb-4 pt-2 pb-lg-2">
          <small>
            ©{new Date().getFullYear()} {content.siteName}. All rights reserved.
            <br />
            Powered by{" "}
            <a
              href="https://doclinkapp.ir"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#10b981", textDecoration: "none" }}
            >
              DocLink
            </a>
          </small>
        </div>
      </div>
      <style>{`
        .footer-layout {
          display: flex;
          flex-direction: column;
          gap: 1.75rem;
        }
        .footer-info-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 1.5rem 2rem;
        }
        .footer-info-grid h5 {
          margin-bottom: 1rem;
        }
        .footer-bottom {
          border-top: 1px solid #333;
          margin-top: 30px;
          padding-top: 20px;
          text-align: center;
        }
        .footer-badges {
          display: flex;
          gap: 15px;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
          margin: 0 0 1.1rem;
        }
        .footer-badges img {
          height: 72px;
          border-radius: 8px;
        }
        .footer-map-block {
          width: 100%;
        }
        .footer-map-card {
          position: relative;
          height: 280px;
          border-radius: 18px;
          overflow: hidden;
          border: 1px solid #333;
        }
        .footer-map-canvas {
          height: 100%;
          width: 100%;
        }
        .footer-map-overlay {
          position: absolute;
          right: 12px;
          left: 12px;
          bottom: 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 10px 14px;
          border-radius: 12px;
          background: rgba(18, 18, 18, 0.82);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(124, 200, 144, 0.22);
        }
        .footer-map-overlay span {
          color: #e8e8e8;
          font-size: 0.85rem;
          line-height: 1.6;
        }
        .footer-map-overlay a {
          flex-shrink: 0;
          color: #121212;
          background: #7cc890;
          text-decoration: none;
          font-weight: 700;
          font-size: 0.8rem;
          padding: 8px 12px;
          border-radius: 10px;
          white-space: nowrap;
        }
        .footer-map-dark .leaflet-container {
          background: #1a1a1a;
        }
        .footer-map-dark .leaflet-tile-pane {
          filter: invert(1) hue-rotate(180deg) brightness(0.92) contrast(0.92) saturate(0.4);
        }
        .footer-map-dark .leaflet-control-attribution {
          background: rgba(30, 30, 30, 0.8);
          color: #9aa0a6;
        }
        .footer-map-dark .leaflet-control-attribution a {
          color: #7cc890;
        }
        .footer-map-dark .leaflet-control-zoom a {
          background: #2b2b2b;
          color: #eee;
          border-bottom-color: #444;
        }
        .footer-map-dark .leaflet-control-zoom a:hover {
          background: #3a3a3a;
        }
        .footer-map-dark .leaflet-bar {
          border-color: #444;
        }
        @media (max-width: 768px) {
          .footer-info-grid {
            grid-template-columns: 1fr;
            gap: 1.25rem;
          }
          .footer-map-card {
            height: 240px;
          }
          .footer-map-overlay {
            flex-direction: column;
            align-items: flex-start;
          }
        }
      `}</style>
    </footer>
  );
};

const iconStyle = {
  margin: "0 10px",
  fontSize: "20px",
  color: "#fff",
  cursor: "pointer",
};

export default InfoFooter;
